/**
 * Tendencia de un área: una línea, sin librería de gráficos.
 *
 * §11. Cuatro o cinco puntos en una escala de 1 a 5 no justifican traerse
 * doscientos kilobytes de una librería de charts a una aplicación que tiene
 * que arrancar sin red. Es un `<svg>` con un `polyline` y unos círculos.
 *
 * Con UN SOLO punto no se dibuja una línea: se muestra el valor. Una recta
 * horizontal a partir de un dato parece información y no lo es.
 */

import type { PuntoDeEvolucion } from '../../domain/reglas/progreso';
import estilos from './Tendencia.module.css';

interface Props {
  puntos: PuntoDeEvolucion[];
  /** Para el texto alternativo: «Listening», «Naturalidad»… */
  nombre: string;
}

const MINIMO = 1;
const MAXIMO = 5;
const ANCHO = 240;
const ALTO = 56;
const MARGEN = 6;

export function Tendencia({ puntos, nombre }: Props) {
  if (puntos.length === 0) return null;

  if (puntos.length === 1) {
    const unico = puntos[0]!;
    return (
      <p className={estilos.unico}>
        <strong>{unico.valor}</strong>/5 en la semana {unico.semana}. Con un solo dato
        todavía no hay tendencia.
      </p>
    );
  }

  const util = ANCHO - MARGEN * 2;
  const alturaUtil = ALTO - MARGEN * 2;
  const paso = util / (puntos.length - 1);

  const coordenadas = puntos.map((punto, indice) => {
    const x = MARGEN + indice * paso;
    // 5 arriba, 1 abajo: más alto es «me salió con más facilidad».
    const proporcion = (punto.valor - MINIMO) / (MAXIMO - MINIMO);
    const y = MARGEN + alturaUtil - proporcion * alturaUtil;
    return { x, y, punto };
  });

  const primero = puntos[0]!;
  const ultimo = puntos[puntos.length - 1]!;
  const resumen = `${nombre}: de ${primero.valor} sobre 5 en la semana ${primero.semana} a ${ultimo.valor} sobre 5 en la semana ${ultimo.semana}.`;

  return (
    <figure className={estilos.figura}>
      <svg
        className={estilos.grafico}
        viewBox={`0 0 ${ANCHO} ${ALTO}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={resumen}
      >
        <polyline
          className={estilos.linea}
          points={coordenadas.map(({ x, y }) => `${x},${y}`).join(' ')}
        />
        {coordenadas.map(({ x, y, punto }) => (
          <circle key={punto.semana} className={estilos.punto} cx={x} cy={y} r={3} />
        ))}
      </svg>
      {/* El texto no es decorativo: es la versión legible del gráfico, y la
          única que funciona si el SVG no se ve. */}
      <figcaption className={estilos.leyenda}>
        Semana {primero.semana}: {primero.valor}/5 · Semana {ultimo.semana}:{' '}
        {ultimo.valor}/5
      </figcaption>
    </figure>
  );
}
